import { cn } from "cn";
import type React from "react";
import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button/button";
import { BwWarningCircleFill } from "@/components/ui/icons/icons.generated";
import { Typography } from "@/components/ui/typography";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class EditorErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log to Sentry (already integrated per AGENTS.md)
    console.error("Editor Error:", error, errorInfo);

    // Optionally send to Sentry if available
    if (typeof window !== "undefined" && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          component: "editor",
        },
      });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className={cn(
            /* sorted */
            "flex flex-col items-center justify-center gap-4",
            "p-8",
            "bg-bg-dark-low border border-border-dark-low rounded-2xl",
          )}
        >
          <BwWarningCircleFill size={48} className="text-text-negative" />
          <div className="flex flex-col items-center gap-2 text-center">
            <Typography size="lg" className="font-semibold text-text-dark-max">
              Editor failed to load
            </Typography>
            <Typography size="sm" className="text-text-dark-high max-w-md">
              The editor encountered an error and couldn't load properly. Please
              try refreshing the page or contact support if the issue persists.
            </Typography>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details
                className={cn(
                  /* sorted */
                  "overflow-auto",
                  "max-w-full",
                  "mt-4 p-4",
                  "text-left",
                  "bg-bg-negative-min rounded-lg",
                )}
              >
                <summary className="cursor-pointer text-sm font-mono text-text-negative">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 text-xs text-text-dark-high whitespace-pre-wrap wrap-break-word">
                  {this.state.error.message}
                  {"\n\n"}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
          <Button onClick={this.handleReset} kind="solid" variant="primary">
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
